import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionPanel,
  Box,
  Button,
  Heading,
  CheckBox,
  FormField,
  Layer,
  TextInput,
  Text,
} from "grommet";
import HotelService from "../../services/hotel.service";
import AuthService from "../../services/auth.service";
import { presentToastSuccess, presentToastErrorContent } from "@shared/Toast";
import { ButtonWithConfirm } from "@features/shared";
import { useFetchHotel } from "./redux/hooks";
import { useTranslation } from "react-i18next";

const defaultValues = {
  fromName: "",
  fromEmail: "",
  smtpHost: "",
  smtpPort: 587,
  smtpSecure: true,
  smtpUser: "",
  smtpPassword: "", // input only; never returned by server
};

export default function SettingsWhitelabel() {
  const { t } = useTranslation();
  const { hotel, fetchHotel } = useFetchHotel();
  const [formValues, setFormValues] = useState(defaultValues);
  const [passwordSet, setPasswordSet] = useState(false);
  const [editPassword, setEditPassword] = useState(false);
  const [autoManage, setAutoManage] = useState(true);
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testEmail, setTestEmail] = useState(
    AuthService.getCurrentUser()?.email || "",
  );

  useEffect(() => {
    const wlc = hotel?.whiteLabelConfig;
    if (wlc) {
      setFormValues({
        fromName: wlc.fromName || "",
        fromEmail: wlc.fromEmail || "",
        smtpHost: wlc.smtpHost || "",
        smtpPort: wlc.smtpPort || 587,
        smtpSecure: !!wlc.smtpSecure,
        smtpUser: wlc.smtpUser || "",
        smtpPassword: "",
      });
      setPasswordSet(!!wlc.passwordSet);
      setEditPassword(!wlc.passwordSet);
      // Consider connection settings "auto-managed" when they match the defaults.
      setAutoManage((wlc.smtpPort || 587) === 587 && !wlc.smtpSecure);
    } else {
      setFormValues(defaultValues);
      setPasswordSet(false);
      setEditPassword(true);
      setAutoManage(true);
    }
  }, [hotel]);

  function handleInputChange({ target: { name, value } }) {
    setFormValues({ ...formValues, [name]: value });
  }

  function handleCheckboxChange({ target: { name, checked } }) {
    setFormValues({ ...formValues, [name]: checked });
  }

  const handleSave = (e) => {
    e.preventDefault();
    const payload = {
      fromName: formValues.fromName,
      fromEmail: formValues.fromEmail,
      smtpHost: formValues.smtpHost,
      smtpPort: autoManage ? 587 : formValues.smtpPort,
      smtpSecure: autoManage ? false : formValues.smtpSecure,
      smtpUser: formValues.smtpUser,
    };
    if (editPassword && formValues.smtpPassword) {
      payload.smtpPassword = formValues.smtpPassword;
    }
    HotelService.updateWhiteLabelConfig(payload).then(
      (response) => {
        if (response.data.ok) {
          presentToastSuccess("Whitelabel settings saved");
          setFormValues({ ...formValues, smtpPassword: "" });
          fetchHotel();
        }
      },
      (error) => presentToastErrorContent(error),
    );
  };

  const handleSendTest = (e) => {
    e.preventDefault();
    if (!formValues.smtpPassword && !passwordSet) {
      presentToastErrorContent({
        message: "Enter a password to test the SMTP connection",
      });
      return;
    }
    if (!formValues.smtpPassword) {
      presentToastErrorContent({
        message:
          "Enter the SMTP password to test (we never display the saved one)",
      });
      return;
    }
    setTestEmail(AuthService.getCurrentUser()?.email || testEmail);
    setTestModalOpen(true);
  };

  const submitTest = () => {
    if (!testEmail.trim()) {
      presentToastErrorContent({ message: "Enter a recipient email" });
      return;
    }
    HotelService.sendWhiteLabelTestEmail({
      to: testEmail.trim(),
      fromName: formValues.fromName,
      fromEmail: formValues.fromEmail,
      smtpHost: formValues.smtpHost,
      smtpPort: autoManage ? 587 : formValues.smtpPort,
      smtpSecure: autoManage ? false : formValues.smtpSecure,
      smtpUser: formValues.smtpUser,
      smtpPassword: formValues.smtpPassword,
    }).then(
      (response) => {
        if (response.data.ok) {
          presentToastSuccess(`Test email sent to ${response.data.sentTo}`);
          setTestModalOpen(false);
        } else {
          presentToastErrorContent({
            message: response.data.error || "Send failed",
          });
        }
      },
      (error) => presentToastErrorContent(error),
    );
  };

  const handleClear = () => {
    HotelService.clearWhiteLabelConfig().then(
      () => {
        presentToastSuccess("SMTP configuration cleared");
        fetchHotel();
      },
      (error) => presentToastErrorContent(error),
    );
  };

  return (
    <Box align="center" pad="medium" flex={false}>
      <Box width={{ max: "large" }} fill="horizontal" flex={false}>
        <Heading level={3} margin="small">
          {t("settingsWhitelabel.title")}
        </Heading>
        <Text margin={{ horizontal: "small" }}>
          Configure your own SMTP server so guest emails are sent from your
          domain. The "From" address used will be:{" "}
          <strong>
            {formValues.fromName || "Hotel"} &lt;
            {formValues.fromEmail || "your@hotel.com"}&gt;
          </strong>
          .
        </Text>
        <Accordion margin={{ top: "xsmall", horizontal: "small" }}>
          <AccordionPanel
            label={
              <Text size="small" weight="bold" color="white">
                ⚠️ SPF &amp; DKIM required — your domain DNS must be configured
                or emails will bounce
              </Text>
            }
            style={{
              background: "#FFAA15",
              borderRadius: 4,
              padding: "4px 8px",
            }}
          >
            <Box background="status-warning" gap="xsmall" pad="small">
              <Text size="small">
                Gmail and most providers reject email from senders who haven't
                proved they own their domain. You need two DNS records on the
                domain you send from. Where you add them depends on who runs
                your domain's DNS (usually your domain registrar — GoDaddy, OVH,
                Namecheap — or your hosting provider — SiteGround, Hostinger,
                etc.).
              </Text>

              <Text size="small" weight="bold" margin={{ top: "xsmall" }}>
                1. SPF record (authorises your SMTP server to send on your
                behalf)
              </Text>
              <Text size="small">
                Add a <strong>TXT</strong> record on your domain root (host{" "}
                <code>@</code> or just your domain name) with a value that tells
                receivers which servers may send for you. The exact value
                depends on your email provider:
              </Text>
              <Box
                background="light-1"
                round
                pad="xsmall"
                margin={{ vertical: "xsmall" }}
                gap="2px"
              >
                <Text size="small" style={{ fontFamily: "monospace" }}>
                  Gandi mail: v=spf1 include:_mailcust.gandi.net ~all
                </Text>
                <Text size="small" style={{ fontFamily: "monospace" }}>
                  Google Workspace: v=spf1 include:_spf.google.com ~all
                </Text>
                <Text size="small" style={{ fontFamily: "monospace" }}>
                  Microsoft 365: v=spf1 include:spf.protection.outlook.com ~all
                </Text>
                <Text size="small" style={{ fontFamily: "monospace" }}>
                  SiteGround: v=spf1 include:_spf.mailspamprotection.com ~all
                </Text>
                <Text size="small" style={{ fontFamily: "monospace" }}>
                  GoDaddy Workspace: v=spf1 include:secureserver.net ~all
                </Text>
                <Text size="small" style={{ fontFamily: "monospace" }}>
                  Zoho Mail: v=spf1 include:zoho.com ~all
                </Text>
                <Text size="small" style={{ fontFamily: "monospace" }}>
                  SendGrid: v=spf1 include:sendgrid.net ~all
                </Text>
                <Text size="small" style={{ fontFamily: "monospace" }}>
                  Mailgun: v=spf1 include:mailgun.org ~all
                </Text>
              </Box>
              <Text size="small">
                Only one SPF record is allowed per domain. If you already have
                one, do not create a second — instead, add the new{" "}
                <code>include:</code> inside the existing record, before the
                final <code>~all</code> or <code>-all</code>. Example with two
                providers:{" "}
                <code>
                  v=spf1 include:_spf.google.com include:sendgrid.net ~all
                </code>
                .
              </Text>

              <Text size="small" weight="bold" margin={{ top: "xsmall" }}>
                2. DKIM record (cryptographic signature)
              </Text>
              <Text size="small">
                DKIM is generated <strong>by your email provider</strong> — you
                can't write it by hand. Find the DKIM section in their admin
                panel and copy the TXT record they give you (host name + value),
                then add it to your domain's DNS at your registrar/host. Common
                locations:
              </Text>
              <Box
                background="light-1"
                round
                pad="xsmall"
                margin={{ vertical: "xsmall" }}
                gap="2px"
              >
                <Text size="small">
                  • <strong>Gandi:</strong> Mail → DKIM
                </Text>
                <Text size="small">
                  • <strong>Google Workspace:</strong> Admin Console → Apps →
                  Google Workspace → Gmail → Authenticate email
                </Text>
                <Text size="small">
                  • <strong>Microsoft 365:</strong> Defender → Email
                  authentication settings → DKIM
                </Text>
                <Text size="small">
                  • <strong>SiteGround:</strong> Site Tools → Email →
                  Authentication
                </Text>
                <Text size="small">
                  • <strong>GoDaddy Workspace Email:</strong> Email Settings →
                  DKIM
                </Text>
                <Text size="small">
                  • <strong>Zoho:</strong> Mail Admin Console → Domains → DKIM
                </Text>
                <Text size="small">
                  • <strong>SendGrid / Mailgun / Postmark:</strong> Sender
                  Authentication / Domains
                </Text>
              </Box>
              <Text size="small">
                The provider will give you a host name like{" "}
                <code>selector1._domainkey</code> and a long TXT value starting
                with <code>v=DKIM1; k=rsa; p=…</code>. Paste them exactly as
                shown into your DNS panel.
              </Text>

              <Text size="small" weight="bold" margin={{ top: "xsmall" }}>
                Where do I edit DNS?
              </Text>
              <Text size="small">
                In the control panel of whoever owns your domain's nameservers —
                usually your registrar (GoDaddy, Namecheap, OVH, Gandi) or your
                host (SiteGround, Hostinger). Look for <em>DNS Management</em>,{" "}
                <em>DNS Zone</em>, or <em>Advanced DNS</em>. Not sure who
                controls DNS? Run <code>whois yourdomain.com</code> or use{" "}
                <a
                  href="https://mxtoolbox.com/SuperTool.aspx"
                  target="_blank"
                  rel="noreferrer"
                >
                  mxtoolbox.com
                </a>{" "}
                and look up your nameservers.
              </Text>

              <Text size="small" margin={{ top: "xsmall" }}>
                After adding both records, wait 15 minutes to a few hours for
                DNS to propagate, then use <strong>Send test email</strong> to
                verify delivery. You can also check your setup at{" "}
                <a
                  href="https://www.mail-tester.com/"
                  target="_blank"
                  rel="noreferrer"
                >
                  mail-tester.com
                </a>
                .
              </Text>
            </Box>
          </AccordionPanel>
        </Accordion>

        <Box
          width={{ max: "large", width: "100%" }}
          gap="medium"
          background="light-2"
          round
          pad="medium"
          margin={{ top: "small" }}
        >
          <FormField label={t("settingsWhitelabel.emailName")} width="medium">
            <TextInput
              name="fromName"
              value={formValues.fromName}
              onChange={handleInputChange}
            />
          </FormField>
          <FormField
            label={t("settingsWhitelabel.emailAddress")}
            width="medium"
          >
            <TextInput
              name="fromEmail"
              type="email"
              value={formValues.fromEmail}
              onChange={handleInputChange}
            />
          </FormField>
          <FormField label={t("settingsWhitelabel.smtpHost")} width="medium">
            <TextInput
              name="smtpHost"
              value={formValues.smtpHost}
              onChange={handleInputChange}
            />
          </FormField>
          <FormField label={t("settingsWhitelabel.username")} width="medium">
            <TextInput
              name="smtpUser"
              value={formValues.smtpUser}
              onChange={handleInputChange}
            />
          </FormField>

          {passwordSet && !editPassword ? (
            <Box direction="row" align="center" gap="small">
              <Text>{t("settingsWhitelabel.passwordSet")}</Text>
              <Button
                label={t("settingsWhitelabel.changePassword")}
                onClick={(e) => {
                  e.preventDefault();
                  setEditPassword(true);
                }}
              />
            </Box>
          ) : (
            <FormField label={t("settingsWhitelabel.password")} width="medium">
              <TextInput
                name="smtpPassword"
                type="password"
                value={formValues.smtpPassword}
                onChange={handleInputChange}
              />
            </FormField>
          )}
          <CheckBox
            checked={autoManage}
            onChange={(e) => setAutoManage(e.target.checked)}
            label={t("settingsWhitelabel.autoManage")}
          />
          {!autoManage && (
            <Box direction="row" align="center" gap="medium">
              <FormField label={t("settingsWhitelabel.port")} width="small">
                <TextInput
                  name="smtpPort"
                  type="number"
                  value={formValues.smtpPort}
                  onChange={handleInputChange}
                />
              </FormField>
              <CheckBox
                name="smtpSecure"
                checked={formValues.smtpSecure}
                onChange={handleCheckboxChange}
                label={t("settingsWhitelabel.useTls")}
              />
            </Box>
          )}
        </Box>

        <Box
          direction="row"
          justify="between"
          margin={{ top: "medium" }}
          gap="small"
        >
          <Button
            label={t("settingsWhitelabel.sendTest")}
            onClick={handleSendTest}
          />
          <Box direction="row" gap="small">
            <ButtonWithConfirm
              label={t("settingsWhitelabel.clear")}
              color="status-error"
              alertTitle={t("settingsWhitelabel.clearConfirmTitle")}
              alertMessage={t("settingsWhitelabel.clearConfirmMessage")}
              buttonOKText={t("settingsWhitelabel.clear")}
              onOK={handleClear}
            />
            <Button
              type="submit"
              label={t("settingsWhitelabel.save")}
              primary
              onClick={handleSave}
            />
          </Box>
        </Box>
      </Box>
      {testModalOpen && (
        <Layer
          position="center"
          onClickOutside={() => setTestModalOpen(false)}
          onEsc={() => setTestModalOpen(false)}
        >
          <Box pad="medium" gap="small" width="medium">
            <Heading level={4} margin="none">
              Send test email
            </Heading>
            <Text size="small">
              Send a test email through your SMTP configuration to verify it
              works.
            </Text>
            <FormField label={t("settingsWhitelabel.recipientEmail")}>
              <TextInput
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
              />
            </FormField>
            <Box direction="row" justify="end" gap="small">
              <Button
                label={t("settingsWhitelabel.cancel")}
                onClick={() => setTestModalOpen(false)}
              />
              <Button
                label={t("settingsWhitelabel.send")}
                primary
                onClick={submitTest}
              />
            </Box>
          </Box>
        </Layer>
      )}
    </Box>
  );
}
